import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Clipboard from '@react-native-clipboard/clipboard';

import { EntryDetails } from '@/api/entries/types/types';
import { EventInfo } from '@/api/events/types/types';
import { Button } from '@/components/ButtonComponent';
import { LineSeparator } from '@/components/LineSeparator';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { EntryCardPromoTag } from '@/components/entry-card';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useLaunchIntercomMessenger } from '@/data/intercom/intercom';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useBetslipNavigation } from '@/feature/betslip-pickem/hooks/use-betslip-navigation';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { mapPicksToBetSlipPick } from '@/feature/betslip-pickem/utils/betslip-utils';
import { ComputedEntryPromotion, useComputeEntryPromos } from '@/feature/entries-pickem/hooks/use-compute-entry-promos';
import { usePickemEntryPromos } from '@/feature/entries-pickem/hooks/use-pickem-entry-promos';
import { getRefundedAmountFromPromo } from '@/hooks/use-entries-utils';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { common } from '@/styles/styles';
import { GameType, Result } from '@/types/api.generated';
import { nflFullSeason } from '@/utils/league';
import { format } from 'date-fns';

type EntryFooterProps = {
    entry?: EntryDetails;
    events: EventInfo[];
};

export const EntryFooter = memo(({ entry, events }: EntryFooterProps) => {
    return (
        <>
            <SizedBox value={16} />
            <ReuseLineupButton entry={entry} events={events} />
            <SizedBox value={32} />
            <EntryDetailsWrapper entry={entry} />
        </>
    );
});

const ReuseLineupButton = memo(({ entry, events }: { entry?: EntryDetails; events: EventInfo[] }) => {
    const { t } = useTranslation(['betslip_pickem', 'lineup_update_modal']);
    const reuseShareLineupEnabled = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.settings.enable_share_and_reuse_lineups
    );
    const replacePicks = useBetslipStore(state => state.actions.replacePicks);
    const { navigateToPickslip } = useBetslipNavigation();
    const betslip = useBetslipStore(store => store.betslip);
    const { showInfoSheet } = useAlerts();

    const picks = useMemo(() => mapPicksToBetSlipPick(entry?.picks ?? [], events), [entry?.picks, events]);

    const addLineupToBetslip = useCallback(() => {
        if (betslip.length > 0) {
            showInfoSheet({
                title: t('lineup_update_modal:replace_lineup_title'),
                description: t('lineup_update_modal:replace_lineup_info'),
                buttonLabel: t('lineup_update_modal:replace_lineup'),
                handlePress: () => {
                    BetrAnalytics.trackEvent(AnalyticsEvent.REUSE_LINEUP_ENTRY_SCREEN);
                    replacePicks(picks, { entryId: entry?.id });
                    navigateToPickslip();
                },
                secondaryLabel: t('lineup_update_modal:keep_lineup'),
            });
        } else {
            BetrAnalytics.trackEvent(AnalyticsEvent.REUSE_LINEUP_ENTRY_SCREEN);
            replacePicks(picks, { entryId: entry?.id });
            navigateToPickslip();
        }
    }, [betslip.length, entry?.id, navigateToPickslip, picks, replacePicks, showInfoSheet, t]);

    if (!reuseShareLineupEnabled || entry?.result) {
        return null;
    }

    return (
        <>
            <Button
                hierarchy={'subtle'}
                label={t('betslip_pickem:reuse_lineup')}
                onPress={addLineupToBetslip}
                testID={`entry-screen-reuse-lineup-button`}
                style={styles.reuseLineupButton}
            />
        </>
    );
});

const EntryDetailsWrapper = memo(({ entry }: { entry?: EntryDetails }) => {
    const { t } = useTranslation('betslip_pickem');
    const insets = useSafeAreaInsets();
    const entryPromos = useComputeEntryPromos(entry);

    const picks = useMemo(() => entry?.picks ?? [], [entry?.picks]);
    const hasFullSeasonPicks = picks?.some(it => nflFullSeason(it.league));

    const showLeaderboards = entry?.gameType === GameType.P2p && !!entry.id;
    return (
        <>
            <Text variant={'headlineSmall'}>{t('entry_details')}</Text>
            <SizedBox value={16} />
            {entryPromos.length > 0 ? (
                <>
                    <Promos entryPromos={entryPromos} result={entry?.result} />
                    <SizedBox value={16} />
                </>
            ) : null}
            <SubmissionLog entry={entry} />
            <SizedBox value={20} />
            {entry?.status === 'PENDING' ? (
                <View>
                    <LineSeparator />
                    <SizedBox value={15} />
                    <Text variant="bodySmall" color={'neutral300'}>
                        {t('entry_delay_info')}
                    </Text>
                    {entry?.gameType === GameType.P2p ? (
                        <Text variant="bodySmall" color={'neutral300'}>
                            {t('p2p_entry_delay_info')}
                        </Text>
                    ) : null}
                    <SizedBox value={15} />
                </View>
            ) : null}
            {entry?.status === 'PENDING' && hasFullSeasonPicks ? (
                <View>
                    <Text variant="bodySmall" color={'neutral300'}>
                        {t('full_season_entry_delay')}
                    </Text>

                    <SizedBox value={15} />
                </View>
            ) : null}
            <LineSeparator />
            <SizedBox value={24} />
            <ContactUs />
            <SizedBox value={showLeaderboards ? insets.bottom + 80 : insets.bottom + 14} />
        </>
    );
});

const ContactUs = () => {
    const { t } = useTranslation(['betslip_pickem', 'common']);
    const { launchIntercomMessenger } = useLaunchIntercomMessenger();

    return (
        <Box
            backgroundColor={ds.colorToken.background.neutral.subtler}
            paddingHorizontal={'s16'}
            paddingVertical={'s20'}
            borderRadius={'r20'}
            alignItems={'center'}
            gap={'s16'}
        >
            <Box gap={'s4'} alignItems={'center'}>
                <Text variant="headlineSmall">{t('get_support_any_time')}</Text>
                <Text variant="bodySmall" textAlign={'center'} color={ds.colorToken.text.secondary}>
                    {t('support_24_7')}
                </Text>
            </Box>
            <Button
                shape={'pill'}
                hierarchy="secondary"
                variant="light"
                label={t('common:contact_us')}
                onPress={launchIntercomMessenger}
            />
        </Box>
    );
};

const Promos = ({ entryPromos, result }: { entryPromos: ComputedEntryPromotion[]; result?: Result }) => {
    const { t } = useTranslation(['betslip_pickem', 'common', 'bets']);
    const { showInfoSheet } = useAlerts();
    const promoTags = usePickemEntryPromos(entryPromos, result);
    const hasBetrBucks = entryPromos.some(promo => promo.id === 'betrBucks');

    return (
        <>
            <Text variant={'titleSmall'}>{t('promos')}</Text>
            <SizedBox value={8} />
            <Box gap={'s8'}>
                {promoTags}
                {hasBetrBucks ? (
                    <EntryCardPromoTag
                        label={t('bets:placed_with_betr_bucks')}
                        onInfoPress={() =>
                            showInfoSheet({
                                title: t('bets:placed_with_betr_bucks'),
                                description: t('bets:placed_with_betr_bucks_description'),
                                buttonLabel: t('common:dismiss'),
                            })
                        }
                    />
                ) : null}
            </Box>
            <SizedBox value={8} />
        </>
    );
};

const SubmissionLog = ({ entry }: { entry: EntryDetails }) => {
    const { t } = useTranslation(['betslip_pickem', 'common', 'bets']);
    const refundedAmount = getRefundedAmountFromPromo(entry);

    return (
        <>
            <Text variant={'titleSmall'}>{t('submission_log')}</Text>
            <SizedBox value={8} />
            <Box flex={1} gap="s8">
                <TouchableOpacity
                    activeOpacity={0.9}
                    style={[common.row, common.justifySpaceBetween]}
                    onLongPress={() => {
                        Clipboard.setString(entry?.id ?? '');
                        Alert.alert('Entry ID copied to clipboard', entry?.id);
                    }}
                >
                    <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                        {t('entry_id')}
                    </Text>
                    <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                        {entry?.id}
                    </Text>
                </TouchableOpacity>
                <Row justifyContent={'space-between'}>
                    <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                        {t('common:submitted')}:
                    </Text>
                    <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                        {entry?.createdDate && Date.parse(entry?.createdDate)
                            ? format(Date.parse(entry?.createdDate), 'MM/dd/yyyy hh:mm aa')
                            : entry?.createdDate}
                    </Text>
                </Row>
                <Row justifyContent={'space-between'}>
                    <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                        {t('common:closed')}:
                    </Text>
                    <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                        {entry?.settlementDate
                            ? format(Date.parse(entry?.settlementDate), 'MM/dd/yyyy hh:mm aa')
                            : '--'}
                    </Text>
                </Row>
                <Row justifyContent={'space-between'} testID={'entry-details-result-info'}>
                    <Text variant="bodySmall">{t('bets:result')}:</Text>
                    <Row alignItems={'center'}>
                        <Text variant="bodySmall">
                            {entry?.status === undefined
                                ? ''
                                : entry?.status === 'PENDING'
                                ? t('common:pending')
                                : entry?.result === 'WIN'
                                ? t('common:won')
                                : entry?.result === 'LOSS'
                                ? t('common:lost')
                                : t('common:voided')}
                        </Text>
                        {refundedAmount ? <Text variant="bodySmall">{` • $${refundedAmount} refunded`}</Text> : null}
                    </Row>
                </Row>
            </Box>
        </>
    );
};

const styles = StyleSheet.create({
    reuseLineupButton: {
        paddingVertical: 10,
    },
});
